import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, FileInput, FileOutput, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../lib/api";

const DATE_LOCALES = {
  en: "en-SG",
  ms: "ms-MY",
  zh: "zh-SG",
};

export function ConverterLogsRedirect() {
  const { id } = useParams();
  return <Navigate to={`/converters/${id}/input-log`} replace />;
}

export default function ConverterLogs({ logType = "input" }) {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [converter, setConverter] = useState(null);
  const [loadedId, setLoadedId] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);
  const isInput = logType !== "output";

  const load = useCallback(async ({ reset = false } = {}) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    if (reset) {
      setConverter(null);
      setLoadedId(null);
      setRecords([]);
    }
    try {
      const endpoint = isInput
        ? `/converters/${id}/files`
        : `/converters/${id}/outputs`;
      const [converterResponse, recordsResponse] = await Promise.all([
        api.get(`/converters/${id}`),
        api.get(endpoint),
      ]);
      if (requestId !== requestIdRef.current) return;
      setConverter(converterResponse.data);
      setLoadedId(id);
      setRecords(Array.isArray(recordsResponse.data) ? recordsResponse.data : []);
    } catch (requestError) {
      if (requestId !== requestIdRef.current) return;
      setConverter(null);
      setLoadedId(null);
      setRecords([]);
      setError({ id, message: formatApiError(requestError.response?.data?.detail) });
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [id, isInput]);

  useEffect(() => {
    load({ reset: true });
    return () => {
      requestIdRef.current += 1;
    };
  }, [load]);

  const language = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const activeConverter = loadedId === id ? converter : null;
  const visibleError = error?.id === id ? error.message : null;
  const storeName = activeConverter?.store_name || t("converters.unknownStore");
  const converterType = normalizeConverterType(
    activeConverter?.converter_type ?? activeConverter?.file_type
  );

  if (loading || (!activeConverter && !visibleError)) {
    return (
      <div className="pg-card p-10 text-center text-slate-400">
        {t("common.loading")}
      </div>
    );
  }

  if (visibleError) {
    return (
      <div data-testid={`converter-${logType}-log-error`}>
        <BackToConverters t={t} />
        <div className="pg-card p-8 text-center">
          <h1 className="text-xl font-bold text-slate-900">{t("converterLogs.loadFailed")}</h1>
          <p className="text-sm text-slate-500 mt-2">{visibleError}</p>
          <div className="flex items-center justify-center gap-3 mt-6">
            <Link
              to="/converters"
              className="px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 font-semibold text-sm flex items-center gap-2"
            >
              <ArrowLeft size={15} /> {t("converterLogs.backToConverters")}
            </Link>
            <button
              onClick={() => load({ reset: true })}
              className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-400 hover:to-blue-500 font-semibold text-sm flex items-center gap-2"
              data-testid={`retry-converter-${logType}-log`}
            >
              <RefreshCw size={15} /> {t("converterLogs.retry")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!activeConverter) return null;

  const Icon = isInput ? FileInput : FileOutput;
  const section = isInput ? "input" : "output";

  return (
    <div data-testid={`converter-${logType}-log-page`}>
      <BackToConverters t={t} />

      <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
            {t(`converterLogs.${section}.breadcrumb`)}
          </div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-slate-900">
            {storeName} · {t(`converterLogs.${section}.title`)}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-slate-500">
            <span className="mono">MID {activeConverter.machine_id || "—"}</span>
            <span aria-hidden="true">·</span>
            <span className="pill pill-info">
              {converterTypeLabel(t, converterType)}
            </span>
            <span className={activeConverter.active === false ? "pill pill-danger" : "pill pill-success"}>
              {activeConverter.active === false ? t("common.inactive") : t("common.active")}
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            {t("converterLogs.singaporeTime")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/converters/${id}/${isInput ? "output-log" : "input-log"}`}
            className="px-4 py-2.5 rounded-lg border border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 font-semibold text-sm flex items-center gap-2"
          >
            {isInput ? <FileOutput size={15} /> : <FileInput size={15} />}
            {t(`converterLogs.${isInput ? "output" : "input"}.title`)}
          </Link>
          <button
            onClick={() => load()}
            disabled={loading}
            className="px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-60 font-semibold text-sm flex items-center gap-2"
            data-testid={`refresh-converter-${logType}-log`}
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            {t("common.refresh")}
          </button>
        </div>
      </div>

      <LogSection
        icon={Icon}
        title={t(`converterLogs.${section}.title`)}
        description={t(`converterLogs.${section}.description`)}
        count={records.length}
      >
        {isInput ? (
          <InputTable
            records={records}
            converterType={converterType}
            language={language}
            t={t}
          />
        ) : (
          <OutputTable
            records={records}
            language={language}
            storeName={storeName}
            t={t}
          />
        )}
      </LogSection>
    </div>
  );
}

function BackToConverters({ t }) {
  return (
    <Link
      to="/converters"
      className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-2 mb-4"
    >
      <ArrowLeft size={15} /> {t("converterLogs.backToConverters")}
    </Link>
  );
}

function InputTable({ records, converterType, language, t }) {
  return (
    <table className="w-full text-sm min-w-[680px]">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50/70">
          <th className="px-5 py-3 font-semibold">{t("converterLogs.columns.filename")}</th>
          <th className="px-5 py-3 font-semibold">{t("converterLogs.columns.arrivalTime")}</th>
          <th className="px-5 py-3 font-semibold">{t("converterLogs.columns.status")}</th>
        </tr>
      </thead>
      <tbody>
        {records.length === 0 ? (
          <EmptyRow colSpan={3} message={t("converterLogs.input.empty")} />
        ) : (
          records.map((file, index) => {
            const normalizedStatus = normalizeInputStatus(
              file.public_status ?? file.input_status ?? file.log_status ?? file.status
            );
            const status =
              normalizedStatus === "awaiting" && converterType !== "dayend_nextday"
                ? "processed"
                : normalizedStatus;
            return (
              <tr key={file.id || `${inputFilename(file)}-${index}`} className="border-t border-slate-100">
                <td className="px-5 py-4 font-semibold text-slate-800 break-all">
                  {inputFilename(file)}
                </td>
                <td className="px-5 py-4 text-slate-600 whitespace-nowrap">
                  {formatSingaporeTime(
                    file.arrival_time ?? file.arrived_at ?? file.received_at ?? file.created_at,
                    language
                  )}
                </td>
                <td className="px-5 py-4">
                  <StatusPill status={status} label={inputStatusLabel(t, status)} />
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}

function OutputTable({ records, language, storeName, t }) {
  return (
    <table className="w-full text-sm min-w-[800px]">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50/70">
          <th className="px-5 py-3 font-semibold">{t("converterLogs.columns.filename")}</th>
          <th className="px-5 py-3 font-semibold">{t("converterLogs.columns.sentTime")}</th>
          <th className="px-5 py-3 font-semibold">{t("converterLogs.columns.store")}</th>
          <th className="px-5 py-3 font-semibold">{t("converterLogs.columns.status")}</th>
        </tr>
      </thead>
      <tbody>
        {records.length === 0 ? (
          <EmptyRow colSpan={4} message={t("converterLogs.output.empty")} />
        ) : (
          records.map((output, index) => (
            <tr key={output.id || `${outputFilename(output)}-${index}`} className="border-t border-slate-100">
              <td className="px-5 py-4 font-semibold text-slate-800 break-all">
                {outputFilename(output)}
              </td>
              <td className="px-5 py-4 text-slate-600 whitespace-nowrap">
                {formatSingaporeTime(
                  output.sent_time ?? output.sent_at ?? output.processed_at ?? output.generated_at ?? output.created_at,
                  language
                )}
              </td>
              <td className="px-5 py-4 text-slate-700">
                {output.store_name || output.store?.name || storeName}
              </td>
              <td className="px-5 py-4">
                <StatusPill status="processed" label={inputStatusLabel(t, "processed")} />
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function LogSection({ icon: Icon, title, description, count, children }) {
  return (
    <section className="pg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center shrink-0">
            <Icon size={18} />
          </div>
          <div>
            <h2 className="font-bold text-slate-900">{title}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{description}</p>
          </div>
        </div>
        <span className="pill pill-info">{count}</span>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </section>
  );
}

function EmptyRow({ colSpan, message }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-5 py-12 text-center text-slate-400">
        {message}
      </td>
    </tr>
  );
}

function StatusPill({ status, label }) {
  const className =
    status === "missing"
      ? "pill pill-danger"
      : status === "awaiting"
        ? "pill pill-warning"
        : "pill pill-success";
  return <span className={className}>{label}</span>;
}

function inputFilename(file) {
  return (
    file.filename ||
    file.original_filename ||
    file.expected_filename ||
    file.source_filename ||
    "—"
  );
}

function outputFilename(output) {
  return output.filename || output.output_filename || output.generated_filename || "—";
}

function normalizeInputStatus(value) {
  const status = String(value || "processed").toLowerCase().replace(/[\s-]+/g, "_");
  if (["missing", "missing_file", "missing_sales"].includes(status)) return "missing";
  if (["awaiting", "pending"].includes(status)) return "awaiting";
  return "processed";
}

function inputStatusLabel(t, status) {
  if (status === "missing") return t("converterLogs.statuses.missingFile");
  if (status === "awaiting") return t("converterLogs.statuses.awaiting");
  return t("converterLogs.statuses.processed");
}

function converterTypeLabel(t, type) {
  if (type === "dayend_nextday") return t("converters.types.nextday");
  return t("converters.types.sameday");
}

function normalizeConverterType(value) {
  const type = String(value || "dayend_sameday").toLowerCase().replace(/[\s-]+/g, "_");
  return type.includes("nextday") || type.includes("next_day")
    ? "dayend_nextday"
    : "dayend_sameday";
}

function formatSingaporeTime(value, language) {
  if (!value || value === "-") return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(DATE_LOCALES[language] || DATE_LOCALES.en, {
    timeZone: "Asia/Singapore",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
}
